import { App, Alert, Table, Button, Input, Space, Col, Row, Tooltip, Modal, Form, InputRef } from 'antd';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupItem, GroupItemStore, GroupMemberItem, GroupType } from 'src/stores/groups-store';
import { AppConfig, container } from 'src/inversify.config';
import { ColumnsType } from 'antd/lib/table';
import { CheckSquareFilled, DeleteOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, MinusCircleOutlined } from '@ant-design/icons';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import { ENV } from 'src/';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { OrganizationItemStore, ORGANIZATIONS_MEMBER_LIMIT } from 'src/stores/organizations-store';
import { isEmailOrGuid } from 'src/core/utils/object';
import { curateString } from 'src/core/utils/object';

const AddGroupMembersModal: FC<{
  onAddMembers: (users: GroupMemberItem[]) => void;
  onClose: () => void;
  exclude?: string[];
  groupOrganizationId: string;
  groupId: string;
  groupType?: GroupType;
}> = (props) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const onAdd = async () => {
    let item = (await form.validateFields()) as any;
    if ((item.members || []).length !== 0) props.onAddMembers((item.members || []).map((x: any) => ({ memberId: x.user.value, name: x.user.label })));
    props.onClose();
  };

  const groupStore = container.get(GroupItemStore);
  const disabledUsersRef = useRef<string[]>([]);
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [currentOrganizationMembers, setCurrentOrganizationMembers] = useState<string[]>([]);
  const [groupUserCount, setGroupUserCount] = useState<number>(0);
  const [formUsers, setFormUsers] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const load = async () => {
      await organizationStore.load(props.groupOrganizationId);
      setCurrentOrganizationMembers((organizationState?.item?.value?.members ?? [])?.map((v) => v.userId) ?? []);
      if (props.groupType === GroupType.License) {
        const users = await groupStore.getUsersAlreadyOnGroupType(props.groupOrganizationId, props.groupType, props.groupId);
        if (users) disabledUsersRef.current = users;
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      className="figma-modal"
      title={<h3 className="figma-modal__title">{t('AddMembersToGroup')}</h3>}
      open={true}
      onOk={onAdd}
      onCancel={props.onClose}
      okButtonProps={{
        style: { width: '45%', float: 'right' },
        disabled:
          formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value)).length + currentOrganizationMembers.length >
          ORGANIZATIONS_MEMBER_LIMIT,
      }}
      cancelButtonProps={{ type: 'default', style: { width: '45%' } }}
      okText={t('AddMembers')}
      cancelText={t('Cancel')}
    >
      <Form layout="vertical" autoComplete="off" form={form}>
        {formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length +
          currentOrganizationMembers.length >
          ORGANIZATIONS_MEMBER_LIMIT && (
          <Alert
            type="error"
            message={t('NewGroupUserCountErrorParam')
              .replace('{0}', `${ORGANIZATIONS_MEMBER_LIMIT}`)
              .replace('{1}', `${currentOrganizationMembers.length}`)
              .replace('{2}', `${formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length}`)}
          />
        )}
        {formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.value).length > 0 && (
          <Alert
            type="warning"
            message={
              <>
                {t('TheFollowingUsersWillBeAddedToTheOrganization')}
                <br />
                <br />
                {formUsers
                  .filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.label)
                  .map((v) => (
                    <>
                      - {v?.label}
                      <br />
                    </>
                  ))}
              </>
            }
          />
        )}
        <Form.Item>
          <Form.List name="members">
            {(fields, { add, remove }) => (
              <>
                <div id="new-organization-users-container" style={{ maxHeight: '40vh', overflowX: 'auto' }}>
                  {fields.map(({ key, name: fieldIndx, fieldKey, ...restField }) => (
                    <Row key={fieldKey}>
                      <Col span={20}>
                        <Form.Item
                          {...restField}
                          style={{ marginRight: 10 }}
                          name={[fieldIndx, 'user']}
                          //fieldKey={[fieldKey, 'user']}
                          rules={[
                            () => ({
                              required: true,
                              validator(_, value) {
                                if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
                                if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                                return Promise.resolve();
                              },
                            }),
                            () => ({
                              validator(_, value) {
                                if (disabledUsersRef.current.includes(value.value)) return Promise.reject(t('UserAlreadyIsLicenseManagerError'));
                                return Promise.resolve();
                              },
                            }),
                            (formInstance) => ({
                              message: t('DuplicatedUserWarning'),
                              validator(_, formItem) {
                                const items: { user: { label: string; value: string } }[] = formInstance.getFieldValue('members');

                                if (formItem === undefined) return Promise.resolve();

                                if (items.filter((item) => item && item.user.value === formItem.value).length > 1) {
                                  return Promise.reject();
                                } else {
                                  return Promise.resolve();
                                }
                              },
                            }),
                          ]}
                        >
                          <UserAutocomplete
                            onChange={(user) => {
                              setFormUsers(form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user));
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ textAlign: 'center' }}>
                        <MinusCircleOutlined
                          style={{ fontSize: 20 }}
                          onClick={() => {
                            setGroupUserCount(groupUserCount - 1);
                            remove(fieldIndx);
                            setFormUsers(form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user));
                          }}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      add();
                      setGroupUserCount(groupUserCount + 1);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t('AddUser')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const GroupMembersTab: FC<{}> = ({ ...props }) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const groupStore = container.get(GroupItemStore);
  const groupState = groupStore.state;
  const [error, setError] = useState('');
  const [addMembersVisible, setAddMembersVisible] = useState(false);
  const { modal } = App.useApp();

  const [selectedRowKeys, setSelectedRowKeys] = useState([] as any[]);

  const searchInputRef = useRef<InputRef>(null);

  const handleSearch = (_: any, confirm: () => void) => {
    confirm();
  };

  const handleSearchReset = (clearFilters: (() => void) | undefined, confirm: (() => void) | undefined) => {
    clearFilters && clearFilters();
    confirm && confirm();
  };

  const onReload = async () => {
    await groupStore.load(groupState.item.value.id);
  };

  const onAddGroupMembers = async (newMembers: GroupMemberItem[]) => {
    const response = await groupStore.addGroupMembers(
      groupState.item.value.id,
      newMembers.map((x) => x.memberId)
    );
    if (response && response?.isSuccess) {
      groupState.set((s) => {
        s.item.members = [
          ...s.item.members,
          ...newMembers.filter((m) => s.item.members.every((sm) => sm.memberId !== m.memberId)).map((m) => ({ memberId: m.memberId, name: m.name })),
        ];
        return s;
      });
      setError('');
      groupStore.clearMessages();
    } else {
      setError(config.environment === ENV.DEV ? groupState.errorMessage.value || t('UnexpectedError') : t('ErrorAddingGroupMembers'));
    }
  };

  const onDeleteGroupMembers = () => {
    const groupIds = [...selectedRowKeys];
    modal.confirm({
      title: <h3>{`${t('Confirmation')}`}</h3>,
      icon: null,
      onOk: async () => {
        const response = await groupStore.deleteGroupMembers(groupState.item.value.id, groupIds);
        if (response?.isSuccess) {
          setSelectedRowKeys([]);
          groupState.set((s) => {
            s.item.members = s.item.members.filter((m) => !groupIds.includes(m.memberId));
            return s;
          });
          setError('');
          groupStore.clearMessages();
        } else {
          setError(groupState.errorMessage.value || t('UnexpectedError'));
        }
      },
      content:
        groupIds.length === 1
          ? `${t('AreYouSureRemoveMember').replace('{0}', `${groupIds.length}`)} `
          : `${t('AreYouSureRemoveMembers').replace('{0}', `${groupIds.length}`)} `,
      okText: groupIds.length === 1 ? t(`Delete member`) : t(`Delete members`),
      cancelText: t('Cancel'),
      cancelButtonProps: { type: 'default', style: { width: 120 } },
    });
  };

  const columns: ColumnsType<GroupMemberItem> = [
    {
      title: t('User'),
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInputRef}
            placeholder={t('Search')}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              disabled={((selectedKeys[0] ?? '') as string).trim().length === 0}
              onClick={() => handleSearch(selectedKeys, confirm)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t('Search')}
            </Button>
            <Button onClick={() => handleSearchReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
              {t('Reset')}
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => curateString(record.name.toString().toLowerCase()).includes(curateString(value.toString().toLowerCase())),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInputRef.current?.select(), 100);
        }
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div>
      <Row style={{ padding: '0 40px' }}>
        <Col lg={12} xs={24}>
          <Row gutter={[8, 0]}>
            {selectedRowKeys.length === 0 && (
              <Col>
                <h3 style={{ marginTop: 12 }}>{t('GroupMembers')}</h3>
              </Col>
            )}
            {selectedRowKeys.length !== 0 && (
              <h3 style={{ marginTop: 12 }}>
                {selectedRowKeys.length} {t('RowsSelected')}
              </h3>
            )}
            {selectedRowKeys.length !== 0 && (
              <CheckSquareFilled style={{ marginLeft: 15, marginBottom: 10, marginTop: 12, fontSize: 18, color: '#AB162B' }} />
            )}
          </Row>
        </Col>
        <Col lg={12} xs={24}>
          <Row justify="end">
            {selectedRowKeys.length !== 0 && (
              <Col>
                <Tooltip placement="top" title={selectedRowKeys.length === 1 ? t('RemoveMember') : t('RemoveMembers')}>
                  <Button icon={<DeleteOutlined />} onClick={onDeleteGroupMembers} type="primary" style={{ marginBottom: 16, width: 56 }}></Button>
                </Tooltip>
              </Col>
            )}
            {selectedRowKeys.length === 0 && (
              <Col>
                <Tooltip placement="top" title={t('Reload')}>
                  <Button
                    className="ant-btn-secondary"
                    icon={<ReloadOutlined style={{ color: '#AB162B' }} />}
                    onClick={onReload}
                    style={{ marginBottom: 16, marginRight: 10, width: 56 }}
                  ></Button>
                </Tooltip>
                <Tooltip placement="top" title={t('AddToMembers')}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAddMembersVisible(true)}
                    style={{ marginBottom: 16, width: 56 }}
                  ></Button>
                </Tooltip>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      {error.length !== 0 && <Alert type="error" message={t(error || '')} />}
      <div>
        <Table rowKey="memberId" rowSelection={rowSelection} columns={columns} dataSource={(groupState.item.value as GroupItem)?.members ?? []} />
      </div>
      {addMembersVisible && (
        <AddGroupMembersModal
          exclude={(groupState.item.value?.members ?? []).map((r) => r.memberId)}
          onAddMembers={onAddGroupMembers}
          onClose={() => setAddMembersVisible(false)}
          groupOrganizationId={groupState.item.value?.organizationId}
          groupId={groupState.item.value.id}
          groupType={groupState.item.value.groupType}
          {...props}
        />
      )}
    </div>
  );
};

export default GroupMembersTab;
